import * as React from 'react';
import type { FieldPath, FieldValues, Path, PathValue, UseControllerProps, UseFormGetValues } from 'react-hook-form';

import type { OwnProps as PlainDropdownProps, DropdownOptionType } from 'af-components/Controls/Dropdown';

import DisplayCell from '../../RHFTableCellComponents/Dropdown/DisplayCell';
import type { CellError } from '../../RHFTableCellComponents/error.types';
import RHFUnregisteredFieldWrapper from '../RHFUnregisteredFieldWrapper';

type Props<
	D extends DropdownOptionType,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
> = {
	rhfId?: number | string;
	getValues: UseFormGetValues<TFieldValues>;
	readonly?: boolean;
	bold?: boolean;
} & UseControllerProps<TFieldValues, TName, TTransformedValues> & PlainDropdownProps<D>;

function Dropdown<
	D extends DropdownOptionType,
	TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TTransformedValues = TFieldValues
>(props: Props<D, TFieldValues, TName, TTransformedValues>) {
	const { name, rhfId, placeholder, readonly, bold, ...rest } = props;

	const renderDropdown = React.useCallback((
		{ value, error }: {
			value: PathValue<TFieldValues, keyof TFieldValues & Path<TFieldValues>>;
			error: Nullable<CellError>;
		}) => (
		<DisplayCell
			bold={bold}
			elementKey={rhfId}
			key={name + rhfId}
			placeholder={placeholder}
			{...rest}
			error={error ?? undefined}
			isWhite={!readonly}
			readonly={readonly}
			value={value}
		/>
	),
		[bold, rhfId, name, placeholder, rest, readonly]
	);

	return (
		<RHFUnregisteredFieldWrapper
			name={name}
			{...rest}
			render={renderDropdown}
		/>
	);
}

export default React.memo(Dropdown) as typeof Dropdown;
